import React, { useEffect } from "react";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { Flex } from "antd";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { useGetLocationData } from "~/hooks/locationData";
import { useSelectorDataConfigurator } from "~/hooks/selectorDataConfigurator";
import { TIdentifier } from "~/model/common";
import { useGetCatalogListLazyQuery } from "~/schemas/generated/graphql";
import { Field } from "~/shared/ui/Field";

export interface ICatalogCreateForm {
  name: string;
  parentId: { value: TIdentifier; label: string };
}

interface CatalogFormProps {
  values: ICatalogCreateForm;
  hasChild: boolean;
  onSubmitHandler: (data: ICatalogCreateForm) => void;
  formId: string;
}

const CatalogForm = ({
  values,
  onSubmitHandler,
  hasChild,
  formId,
}: CatalogFormProps) => {
  const { t } = useTranslation();
  const { currentId } = useGetLocationData();

  const schema = yup.object().shape({
    name: yup
      .string()
      .max(250, t("validation.more250"))
      .required(t("validation.required")),
  });

  const { control, handleSubmit, reset, errors } = useForm<ICatalogCreateForm>({
    defaultValues: values,
    resolver: yupResolver(schema),
  });

  const { rowsForSelect, introspectionHandler, searchValue } =
    useSelectorDataConfigurator({
      queryGenerator: useGetCatalogListLazyQuery,
      responseDataGetterString: "DocumentGetter.getSections.items",
      searchConfig: (searchText) => ({ name: searchText }),
    });

  useEffect(() => {
    reset(values);
  }, [values]);

  const handlePopupScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.target as HTMLDivElement;
    if (target.scrollTop + target.clientHeight >= target.scrollHeight - 10) {
      introspectionHandler();
    }
  };

  return (
    <form id={formId} onSubmit={handleSubmit(onSubmitHandler)}>
      <Flex vertical gap={10}>
        <Field
          type="input"
          name="name"
          control={control}
          label={"Название"}
          error={errors["name"]?.message}
        />
        {!hasChild && (
          <Field
            type="select"
            name="parentId"
            control={control}
            label={"Родительский каталог"}
            options={[
              { value: "", label: "Не выбран" },
              ...rowsForSelect.filter((el) => el.value !== currentId),
            ]}
            labelInValue
            showSearch
            filterOption={false}
            onSearch={searchValue}
            onPopupScroll={handlePopupScroll}
          />
        )}
      </Flex>
    </form>
  );
};

export default CatalogForm;
